import CheckIcon from "@mui/icons-material/CheckRounded";
import { Box, ListItemIcon, MenuItem, Select, Stack, Tooltip, Typography } from "@mui/material";
import { green } from "@mui/material/colors";
import useDefinedContext from "@entrilia/shared/hooks/useDefinedContext";
import type { OrganizationRole } from "../../../../../api/types/userManagementTypes";
import OrganizationRoleStatusTag from "../OrganizationRoleStatusTag";
import { organizationRolesDataMap } from "../definitions";
import InviteMembersContext from "./InviteMembersContext";

interface Option {
  value: OrganizationRole;
  text: string;
  description: string;
}

interface RoleItemProps {
  isSelected: boolean;
  value: OrganizationRole;
  description: string;
}

const options: Option[] = Object.entries(organizationRolesDataMap).map(([key, value]) => ({
  value: key as OrganizationRole,
  text: value.label,
  description: value.description,
}));

const RoleItem = ({ value, description, isSelected }: RoleItemProps) => (
  <Stack spacing={1} sx={{ mt: 1, width: "100%" }}>
    <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center" }}>
      <OrganizationRoleStatusTag role={value} />
      {isSelected && (
        <ListItemIcon sx={{ minWidth: "unset", color: green[500] }}>
          <CheckIcon />
        </ListItemIcon>
      )}
    </Stack>
    <Typography sx={{ pl: 0.25, color: (theme) => `${theme.palette.text.secondary} !important` }} variant="caption">
      {description}
    </Typography>
  </Stack>
);

const OrganizationRoleSelector = () => {
  const { organizationRole, updateOrganizationRole, allowedOrganizationRoles, disabledOrganizationRoles } =
    useDefinedContext(InviteMembersContext);

  const renderValue = (selected: string) => {
    const selectedOption = options.find((option) => option.value === selected);
    return selectedOption ? (
      <Typography>{selectedOption.text}</Typography>
    ) : (
      <Typography color="textSecondary">Select user role</Typography>
    );
  };

  const roleOptions = options.filter((op) => allowedOrganizationRoles.includes(op.value));

  const handleChange = (value: OrganizationRole) => {
    // The tooltip wrapper re-enables pointer events (see below), so a click on a disabled
    // role still bubbles to the item's onClick — selection of a disabled role is blocked here.
    if (disabledOrganizationRoles[value]) {
      return;
    }
    updateOrganizationRole(value);
  };

  return (
    <Select
      fullWidth
      displayEmpty
      value={organizationRole || ""}
      onChange={(e) => handleChange(e.target.value)}
      renderValue={renderValue}
    >
      {roleOptions.map((option) => {
        const disabledReason = disabledOrganizationRoles[option.value];
        return (
          <MenuItem key={option.value} value={option.value} disabled={!!disabledReason}>
            {disabledReason ? (
              <Tooltip title={disabledReason}>
                {/* span anchors the tooltip and re-enables pointer events, which the
                    disabled MenuItem otherwise removes from itself and its descendants */}
                <Box component="span" sx={{ width: "100%", pointerEvents: "auto" }}>
                  <RoleItem {...option} isSelected={organizationRole === option.value} />
                </Box>
              </Tooltip>
            ) : (
              <RoleItem {...option} isSelected={organizationRole === option.value} />
            )}
          </MenuItem>
        );
      })}
    </Select>
  );
};

export default OrganizationRoleSelector;
